import React, { useCallback, useEffect, useState, memo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import { Layout } from 'syngenta-digital-cropwise-react-ui-kit';
import IntegrationsActiveIcon from 'assets/integrations_active.svg';
import IntegrationsIcon from 'assets/integrations.svg';
import Collapse from 'assets/sidebar_collapse.svg?react';
import Expand from 'assets/sidebar_expand.svg?react';
import { ResourceMapperMenuIcon, MenuItemIcon } from 'components/icons';
import { RoutePaths } from 'routes/history';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  evaluateBooleanFeatureFlag,
  LDFeatureFlagEnum,
} from 'tools/feature-flags/launchdarkly-flags';
import {
  AmplitudeEventEnum,
  AmplitudePropertyKeyEnum,
  useAmplitude,
} from 'services/amplitudeHelper';
import { RocketOutlined } from '@ant-design/icons';
import { APIState } from '../../../models/APIState';
import FieldMatchingMenuIcon from '../../../components/icons/FieldMatchingMenuIcon';

const { Sider } = Layout;

const getSelectedMenu = (menuName: string) => {
  switch (menuName) {
    case 'integrations':
    case 'configuration':
      return RoutePaths.Integrations();
    case 'resource-mapper':
      return RoutePaths.ResourceMapperView();
    case 'demo-mfe':
      return RoutePaths.DemoMFE();
    case 'field-matching':
      return RoutePaths.FieldMatchingView();
    default:
      return RoutePaths.Integrations();
  }
};

type SidebarMenuProps = {
  unmappedResourcesStatus: APIState;
};

export const SidebarMenu = memo(({ unmappedResourcesStatus }: SidebarMenuProps) => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [selectedMenu, setSelectedMenu] = useState(getSelectedMenu(''));

  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  const trackEvent = useAmplitude();

  const flags = useFlags();
  const isEnabledMfeDemoPage = evaluateBooleanFeatureFlag(
    flags,
    LDFeatureFlagEnum.SHOW_MFE_DEMO_PAGE,
  );
  const isEnabledFieldMatching = evaluateBooleanFeatureFlag(
    flags,
    LDFeatureFlagEnum.ENABLE_FIELD_MATCHING_PAGE,
  );
  const onCollapse = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  const triggerExpandButton = <Expand />;
  const triggerCloseButton = <Collapse />;

  useEffect(() => {
    if (location) {
      const locationItems = (location as any).pathname.split('/');

      let routeName = locationItems[1];
      if (routeName === 'analytics') {
        routeName = locationItems[2];
      }
      setSelectedMenu(getSelectedMenu(routeName));
    }
  }, [location]);

  const connectMenuList = [
    {
      name: t('Integration.int'),
      label: t('Integration.int'),
      key: RoutePaths.Integrations(),
      icon: (
        <div className="flex items-center h-100%">
          <MenuItemIcon
            data-cy="sidebar_integrations_icon"
            activeIcon={IntegrationsActiveIcon}
            icon={IntegrationsIcon}
            alt="integrations icon"
            selected={selectedMenu === RoutePaths.Integrations()}
          />
        </div>
      ),
      onClick: () => {
        trackEvent(AmplitudeEventEnum.ACCESS_FEATURE, {
          [AmplitudePropertyKeyEnum.FEATURE]: 'Integrations',
        });
        history.push(RoutePaths.Integrations());
      },
      enabled: true,
    },
    {
      name: t('Data Matching'),
      label: t('Data Matching'),
      key: RoutePaths.ResourceMapperView(),
      icon: (
        <div className="flex items-center h-100%">
          <ResourceMapperMenuIcon
            className="max-w-none"
            selected={selectedMenu === RoutePaths.ResourceMapperView()}
            unmappedResourcesStatus={unmappedResourcesStatus}
          />
        </div>
      ),
      onClick: () => {
        trackEvent(AmplitudeEventEnum.ACCESS_FEATURE, {
          [AmplitudePropertyKeyEnum.FEATURE]: 'Data Matching',
        });
        history.push(RoutePaths.ResourceMapperView());
      },
      enabled: true,
    },
    {
      name: t('field_matching.menu.title'),
      label: t('field_matching.menu.title'),
      key: RoutePaths.FieldMatchingView(),
      icon: (
        <div className="flex items-center h-100%">
          <FieldMatchingMenuIcon
            className="submenuIcon max-w-none"
            selected={selectedMenu === RoutePaths.FieldMatchingView()}
          />
        </div>
      ),
      onClick: () => {
        trackEvent(AmplitudeEventEnum.ACCESS_FEATURE, {
          [AmplitudePropertyKeyEnum.FEATURE]: 'field_matching.menu.title',
        });
        history.push(RoutePaths.FieldMatchingView());
      },
      enabled: isEnabledFieldMatching,
    },
    {
      name: 'Demo MFE',
      label: 'Demo MFE',
      key: RoutePaths.DemoMFE(),
      icon: (
        <div className="flex items-center justify-center h-100% w-24px">
          <RocketOutlined data-cy="sidebar_demo_mfe_icon" size={24} color="#696F87" />
        </div>
      ),
      onClick: () => {
        history.push(RoutePaths.DemoMFE());
      },
      enabled: isEnabledMfeDemoPage,
    },
  ];

  const connectMenuItems = connectMenuList
    .filter(({ enabled }) => enabled)
    .map((props: any) => (
      <Menu.Item {...props} enabled={props.enabled?.toString()}>
        {t(props.label)}
      </Menu.Item>
    ));

  return (
    <Sider
      theme="dark"
      trigger={collapsed ? triggerExpandButton : triggerCloseButton}
      width="226"
      collapsedWidth="64"
      collapsible={true}
      collapsed={collapsed}
      onCollapse={onCollapse}
      data-testid="sidebar"
      className={'sidebar'}
    >
      <Menu
        style={{ width: collapsed ? 64 : 226 }}
        theme="dark"
        selectedKeys={[selectedMenu]}
        mode="inline"
        className="sidebar pt-24 overflow-auto h-100% fixed bg-neutral-90"
      >
        {connectMenuItems}
      </Menu>
    </Sider>
  );
});

SidebarMenu.displayName = 'SidebarMenu';
